import { forwardRef, memo, useMemo, type ElementType, type MouseEventHandler, type ForwardedRef } from 'react';
import size from 'lodash/size';
import clsx from 'clsx';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import { type TooltipProps } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import Box, { type BoxProps } from '@mui/material/Box';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
// Material Icon imports
import Bullseye from 'mdi-material-ui/Bullseye';
// local imports
import SimpleTooltip from './SimpleTooltip';
// SCSS imports
import { avatar, avatarSmall, enabled, muted, inProgress } from './TargetIcon.module.scss';

type TargetIconProps = {
  readonly active?: boolean | null;
  readonly pending?: boolean | null;
  readonly disabled?: boolean | null;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly className?: string;
  readonly small?: boolean;
  readonly tooltip?: string;
  readonly tooltipPlacement?: TooltipProps['placement'];
  readonly label?: string;
  readonly activeLabel?: string;
  readonly Icon?: ElementType;
  readonly ActiveIcon?: ElementType;
  readonly sx?: AvatarProps['sx'];
}

const TargetIcon = forwardRef<HTMLButtonElement, TargetIconProps>(({
  active = false,
  pending = false,
  onClick,
  disabled,
  className,
  small = false,
  tooltip,
  tooltipPlacement,
  label,
  activeLabel,
  Icon,
  ActiveIcon,
  sx
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const AltIcon = (active && ActiveIcon) || Icon;
  const icon = AltIcon ? (pending && (
    <CircularProgress
        ref={onClick ? undefined : ref}
        sx={onClick ? undefined : sx}
        size={24}
        color="primary"
    />
  )) || (
    <AltIcon
        ref={onClick ? undefined : ref}
        sx={onClick ? undefined : sx}
        color={disabled ? 'disabled' : 'primary'}
    />
  ) : (
    <Avatar
        ref={onClick ? undefined : ref as ForwardedRef<HTMLDivElement>}
        sx={onClick ? undefined : sx}
        variant="circular"
        className={clsx(className, {
          [avatar]: !small,
          [avatarSmall]: small,
          [enabled]: active,
          [muted]: !active && !pending,
          [inProgress]: pending
        })}
    >
      {pending
      ? <CircularProgress size={small ? 20 : 28} color="primary"/>
      : <Bullseye fontSize="inherit"/>}
    </Avatar>
  );

  const lbl = active ? activeLabel || 'common.button.targeted' : label || (onClick ? 'common.button.not_targeted' : undefined);
  const srLabel = lbl ? (
    <Box sx={visuallyHidden as BoxProps['sx']}>
      <FormattedMessage id={lbl} defaultMessage={lbl}/>
    </Box>
  ) : undefined;

  const tooltipText = useMemo(
    () => tooltip && size(tooltip) >= 1 ? formatMessage({ id: tooltip, defaultMessage: tooltip }) : undefined,
    [tooltip, formatMessage]);

  const content = onClick ? (
    <IconButton
        ref={ref}
        sx={sx}
        onClick={onClick}
        disabled={disabled || pending ? true : undefined}
        className={AltIcon ? className : undefined}
    >
      {icon}
      {srLabel}
    </IconButton>
  ) : undefined;

  return tooltipText ? (
    <>
      <SimpleTooltip title={tooltipText} placement={tooltipPlacement} disableInteractive>
        {content || icon}
      </SimpleTooltip>
      {content ? undefined : srLabel}
    </>
  ) : content || (
    <>
      {icon}
      {srLabel}
    </>
  );
});

TargetIcon.displayName = 'TargetIcon';

export default memo(TargetIcon);
